<template>
    <web-entry-selector-layout :title="title" :show-nav="availableModes.length > 1" :modes="availableModes" :error="validationError" :agencies="communityPolicingStore.getOrisForSelectList" @ori-change="handleOriChange" @mode-change="handleModeChange" @submit="handleSubmit">
        <template v-slot:extra-fields-delete>
            <InformationModal title="Delete Confirmation" :show-footer="true" :show-modal="showModal" @dismiss="handleModalDismiss" >
                <template v-slot:body>
                    Are you sure you want to delete this stop?
                </template>
                <template v-slot:footer>
                    <div class="hstack gap-2">
                        <button type="button" class="btn btn-primary w-50" @click.prevent="submitDelete()">Ok</button>
                        <button type="button" class="btn btn-secondary w-50" @click.prevent="handleModalDismiss()">Cancel</button>                        
                    </div>
                </template>
            </InformationModal>  
          <div class="mt-1">
                <label class="form-label mb-1 fw-bolder">Year:</label>
                <SelectField v-model="year" :select-items="years" :is-horizontal="false" :is-required="true" @update:model-value="handleYearUpdate($event)" :ref="(el) => { addValidatableComponent(el) }">
                </SelectField>
            </div>
            <div class="mt-1">
                <label class="form-label mb-1 fw-bolder">Stop Number:</label>
                <input type="text" class="form-control form-text" v-model="idInput" :required="true" aria-required="true" />
            </div>
        </template>
        <template v-slot:extra-fields-zero>
            <div class="mt-1">
                <label class="form-label mb-1 fw-bolder">Year:</label>
                <SelectField v-model="year" :select-items="years" :is-horizontal="false" :is-required="true" @update:model-value="handleYearUpdate($event)" :ref="(el) => { addValidatableComponent(el) }">
                </SelectField>
            </div>
            <div class="mt-1">
                <label class="form-label mb-1 fw-bolder">Month:</label>
                <SelectField v-model="month" :select-items="months" :is-horizontal="false" :is-required="true">
                </SelectField>
            </div>
        </template> 
    </web-entry-selector-layout>
</template>
<script setup lang="ts">

import { computed, ref, watch } from 'vue';
import SelectField from '@/components/Misc/SelectField.vue';
import { SelectEntryType, SelectItem, SelectorMode } from '@/types';
import WebEntrySelectorLayout from '@/components/BSelector/WebEntrySelectorLayout.vue';
import { useOriStore } from '@/stores/oriStore';
import useValidation from '@/components/Misc/validationUtils';
import { handleErrorInResponse } from '@/utils';
import InformationModal from '@/components/Misc/InformationModal.vue';
import { useCommunityPolicingStore } from '@/stores/communityPolicingStore';

const props = defineProps<{
    title:string,
    url:string,
    allowZeroReport:boolean,
    allowDeleteReport:boolean
}>()
const {validatableComponentArray, triggerValidation, addValidatableComponent, resetValidatableComponentArray } = useValidation();

const ori = ref("");
const idInput = ref("");
const year = ref(0);
const years = ref([] as SelectItem[]);
const month = ref(-1);
const months = ref([] as SelectItem[]);
const currentMode = ref({} as SelectEntryType);
const availableModes = ref([] as SelectorMode[]);
const showModal = ref(false);
const validationError = ref("");

if(props.allowZeroReport){
    availableModes.value.push({type:SelectEntryType.Zero, label:'Zero Report', isDefaultSelection: true});
}

if(props.allowDeleteReport){
    availableModes.value.push({type:SelectEntryType.Delete, label:'Delete Report', isDefaultSelection: false});
}

const communityPolicingStore = useCommunityPolicingStore();

const isValid = (mode:SelectEntryType):Boolean => {
    if(mode == SelectEntryType.Zero){
        return year.value > 0 && month.value > 0;
    }
    else if(mode == SelectEntryType.Delete){
        return year.value > 0 && idInput.value != "";
    }         
    
    return true;
}

defineExpose({ isValid })

function handleOriChange(newOri) {
    communityPolicingStore.setSelectedOri(newOri);
    communityPolicingStore.fetchDatasetDetailsForOri().then(()=> {
        generateYearArray();
    });    
}

function handleModeChange(mode:SelectEntryType){
    
    currentMode.value = mode;
    generateYearArray();
    year.value = 0;
    month.value = -1;
    idInput.value = '';
}

function handleSubmit(ori:any, mode:SelectEntryType) {    
        if(mode == SelectEntryType.Zero) {
            
            communityPolicingStore.submitZeroReport(ori.value, year.value, month.value)
                .then((response) => {
                    window.location.href = 'dataupload.aspx?source=6';
                }).catch(function (error) {
                    handleErrorInResponse(error);
                });
        }
        else if(mode == SelectEntryType.Delete) {
            showModal.value = true;
        }    
}

function submitDelete(){
    let id = idInput.value;
    let ori = communityPolicingStore.getSelectedOri.value;
    let stopYear = year.value;

    communityPolicingStore.validateStopInfo(id,ori,stopYear).then((result) => {    
        if(result && result.data) {
            communityPolicingStore.submitDelete(id,ori,stopYear)
            .then((response) => {
                window.location.href = 'dataupload.aspx?source=6';
            }).catch(function (error) {
                handleErrorInResponse(error);
            });
        }
        else {
            validationError.value = "The record couldn't be located using the information that was provided. Please verify your information and try the request again.";
            showModal.value = false;
        }
    });
}

function generateYearArray() {   
    var data = communityPolicingStore.getDetailsForCurrentDataset;
    
    years.value = [] as SelectItem[];

    if(data != undefined){
        let endDate = data.agency_end_date != undefined ? new Date(data.agency_end_date) : new Date(Date.now());

        //Since we can't submit a zero report for the current month were going to hide the current year is we're still in january otherwise the month drop down is blank.
        if(currentMode.value == SelectEntryType.Zero && endDate.getFullYear() === new Date(Date.now()).getFullYear() && endDate.getMonth() === 0){
            endDate.setFullYear(endDate.getFullYear() -1, 11);
        }

        let startDate = data.agency_start_date != undefined ? new Date(data.agency_start_date) : new Date(data.start_date);

        for(let i = endDate.getFullYear(); i >= startDate.getFullYear(); i--){
            years.value.push(({label:`${i}`, value:i} as SelectItem))
        }

        if(years.value.length > 0){
            year.value = years.value[0].value;
            handleYearUpdate(year.value);
        }
    }    
};

function generateMonthArray() {
    if(year.value === 0){
        return [];
    }

    var data = communityPolicingStore.getDetailsForCurrentDataset;
    months.value = getMonthsAsSelectItemArray();
    
    let endDate = data.agency_end_date != undefined ? new Date(data.agency_end_date) : new Date(Date.now());
    let startDate = data.agency_start_date != undefined ? new Date(data.agency_start_date) : new Date(data.start_date);
    let filtered = months.value;

    if(startDate.getFullYear() == endDate.getFullYear()){
        filtered = months.value.filter((item) => { 
            if(item.value > startDate.getMonth() && item.value <= endDate.getMonth()) 
                return item;
        });
    }
    else{
        if (year.value == endDate.getFullYear()) {
            filtered = months.value.filter((item) => 
            {
                if(item.value <= endDate.getMonth())
                    return item;
            });
        }
        else if(year.value == startDate.getFullYear()){
            filtered = months.value.filter((item) => 
            {
                if(item.value > startDate.getMonth()) 
                    return item;
            
            });
        }
        else {
            filtered = months.value;
        }
    }
        
    months.value = filtered.sort((n1,n2) => {
            if (n1.value < n2.value) {
                return 1;
            }
            if (n1.value > n2.value) {
                return -1;
            }
            return 0;
        });

};

function getMonthsAsSelectItemArray(){
    const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ] as const;
      
    let monthsArray = [] as SelectItem[];
    
    monthNames.map((value, index) => {
        monthsArray.push({label: value, value: index + 1} as SelectItem);
    });
    
    return monthsArray;
}

function handleYearUpdate(newYear){
    generateMonthArray();
}

function handleModalDismiss(){
    showModal.value = false;
}

</script>